import storage from 'dibs-browser-storage';
import {
    VISITS_PER_SESSION_LOGIN_PROMPT,
    VISITS_PER_SESSION_LOGIN_PROMPT_SB_LANDING,
    PSEUDO_SESSION_EXPIRES_AT,
    KEY_STORAGE,
    KEY_PSEUDO_SESSION_STORAGE,
    KEY_SESSION_INITIATED,
    KEY_SESSION_STORAGE,
    KEY_SESSION_PAGE_COUNT,
} from './visitCountConstants';

export class VisitCountModel {
    constructor() {
        this.data = {
            totalVisitCount: 0,
            session: 0,
            sinceRegPrompt: 0,
            regPromptLimit: VISITS_PER_SESSION_LOGIN_PROMPT,
            sbPromptLimit: VISITS_PER_SESSION_LOGIN_PROMPT_SB_LANDING,
            pseudoSessionExpiration: PSEUDO_SESSION_EXPIRES_AT,
            gaSessionPageCount: 0,
            priorPagePdp: 0,
            gaTrackerEventFired: 0,
            sessionInitiated: 'true',
            sessionVisitCount: 0,
        };

        if (storage.localStorage) {
            this.parseIntStorageData(KEY_STORAGE);
            this.parseIntStorageData(KEY_PSEUDO_SESSION_STORAGE);
            this.parseIntStorageData(KEY_SESSION_STORAGE);
            if (storage.sessionStorage) {
                this.parseIntSessionData(KEY_SESSION_PAGE_COUNT);
            }

            if (this.isPseudoSessionExpired()) {
                this.data = {
                    session: 0,
                    regPromptLimit: VISITS_PER_SESSION_LOGIN_PROMPT,
                    sbPromptLimit: VISITS_PER_SESSION_LOGIN_PROMPT_SB_LANDING,
                    gaSessionPageCount: 0,
                    priorPagePdp: 0,
                    gaTrackerEventFired: 0,
                    sessionInitiated: 'true',
                    sessionVisitCount: 0,
                };
            }

            this.setSessionData();
        }
    }

    /**
     * checks to see if time stamp on "session" storage has expired
     * @returns {boolean}
     */
    isPseudoSessionExpired() {
        const limit = this.data.pseudoSessionExpiration;
        const now = new Date().getTime();
        // if no timestamp assume it's brand new
        return now - (this.data.pseudoTimeStamp || now) >= limit;
    }

    /**
     * fetches an object from local storage and parses its fields to integers
     * @param key {string} the key in local storage
     */
    parseIntStorageData(key) {
        const source = storage.localStorage;

        const data = source.getItem(key) || {};

        // parseInt over all fields
        Object.keys(data).forEach(field => {
            const values = parseInt(data[field], 10) || 0;
            this.set(field, values);
        });
    }

    /**
     * fetches an object from session storage and parses its fields to integers
     * @param key {string} the key in session storage
     */
    parseIntSessionData(key) {
        const source = storage.sessionStorage;
        const data = source.getItem(key) || 0;
        this.set(key, data);
    }

    /**
     * gets value from class object
     *  @param field {string} - class object property
     *  @returns object property
     */
    get(field) {
        return this.data[field];
    }

    /**
     * sets value to class object
     * @params field {string}
     * @params value {number}
     */
    set(field, value) {
        this.data[field] = value;
    }

    /**
     * increments tracking related visitor model values
     */
    incrementTracking() {
        const incrementValues = [
            'session',
            'totalVisitCount',
            'sinceRegPrompt',
            'gaSessionPageCount',
        ];
        incrementValues.forEach(value => {
            this.increment(value);
        });
    }

    incrementSessionData() {
        const incrementValues = [KEY_SESSION_PAGE_COUNT];
        incrementValues.forEach(value => {
            this.increment(value);
        });
    }

    setSessionData() {
        const sessionVisitCount = storage.localStorage.getItem('sessionVisitCount') || 0;
        // dibs-storage converts sessionInitiated value to boolean
        const sessionInitiated = storage.sessionStorage.getItem('sessionInitiated') || false;

        // If no ongoing session, increment sessions count, since new session initiated.
        // If no previous sessions tracked, set number of sessions to 1.
        if (!sessionInitiated || !sessionVisitCount) {
            this.set('sessionVisitCount', sessionVisitCount + 1);
        } else {
            this.set('sessionVisitCount', sessionVisitCount);
        }
    }

    /**
     * increments a field by 1
     * @param field {string}
     */
    increment(field) {
        const val = this.get(field) || 0;
        this.set(field, val + 1);
    }

    /**
     * saves visit counts to local and session storage
     */
    save() {
        const localData = {
            totalVisitCount: this.get('totalVisitCount'),
            sinceRegPrompt: this.get('sinceRegPrompt'),
            regPromptLimit: this.get('regPromptLimit'),
        };

        storage.localStorage.setItem(KEY_STORAGE, localData);

        const localSessionVisitCount = this.get(KEY_SESSION_PAGE_COUNT);
        storage.sessionStorage.setItem(KEY_SESSION_PAGE_COUNT, localSessionVisitCount);

        const pseudoSessionData = {
            session: this.get('session'),
            pseudoTimeStamp: new Date().getTime(),
            gaSessionPageCount: this.get('gaSessionPageCount'),
            priorPagePdp: this.get('priorPagePdp'),
            gaTrackerEventFired: this.get('gaTrackerEventFired'),
        };

        storage.localStorage.setItem(KEY_PSEUDO_SESSION_STORAGE, pseudoSessionData);

        // FINDING-13807: Keep track of number of sessions
        storage.localStorage.setItem(KEY_SESSION_STORAGE, this.get('sessionVisitCount'));
        storage.sessionStorage.setItem(KEY_SESSION_INITIATED, this.get('sessionInitiated'));
    }

    /**
     * removes visit counts from session and local storage
     */
    destroy() {
        storage.localStorage.deleteItem(KEY_STORAGE);
        storage.localStorage.deleteItem(KEY_PSEUDO_SESSION_STORAGE);
    }
}
